import { useEffect, useState } from "react";
import {
  acceptInvitation,
  invitationDetail,
  listMyTenants,
  saveSession,
  type InvitationDetail,
  type Session,
} from "./api/client";

/**
 * Claiming an invited account (§3.3).
 *
 * Reached from a link in an email by somebody who has no account yet, so it
 * runs before sign-in and outside the shell: there is no tenant to pick, no
 * navigation to show, and nothing they can do here except choose a password.
 *
 * The password is never transported. Whoever sent the invitation does not know
 * it and cannot be made to reveal it, which is the whole reason this exists
 * rather than an administrator typing one in and passing it along.
 */
export default function AcceptInvitation({ token, onSignedIn }: { token: string; onSignedIn: (s: Session) => void }) {
  const [detail, setDetail] = useState<InvitationDetail | null>(null);
  const [invalid, setInvalid] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    invitationDetail(token)
      .then((d) => live && setDetail(d))
      .catch(() => live && setInvalid(true));
    return () => {
      live = false;
    };
  }, [token]);

  const tooShort = password.length > 0 && password.length < 12;
  const mismatch = confirm.length > 0 && confirm !== password;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      // The reply carries a token for callers that are not browsers; this one
      // is authenticated by the cookies the same call set.
      await acceptInvitation(token, password);
      // Accepting returns a session, so there is no reason to send somebody
      // back to a login form to type the password they just chose.
      // Accepting sets the same cookies a login does; the token in the reply is
      // for callers that are not browsers.
      const base: Session = { tenantId: "", email: detail?.email };
      saveSession(base);
      const tenants = await listMyTenants();
      const first = tenants[0];
      if (!first) {
        setError("The account was created but has no tenant memberships. An administrator has to grant access.");
        setBusy(false);
        return;
      }
      const full: Session = {
        ...base,
        tenantId: first.tenant_id,
        tenantName: first.name,
        role: first.role,
        platform: first.platform,
      };
      saveSession(full);
      // Drop the token from the address bar: it is spent, and a link in
      // somebody's history that looks live is a link they will try again.
      window.history.replaceState(null, "", "/");
      onSignedIn(full);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  if (invalid) {
    return (
      <div className="signin">
        <div className="card">
          <h1>Invitation</h1>
          <div className="banner error" style={{ marginBottom: 0 }}>
            This link is not valid. Invitations expire, work once, and can be withdrawn — ask whoever
            invited you to send another.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="signin">
      <form className="card" onSubmit={submit}>
        <img
          className="logo"
          src="/assets/heimdall-logo.jpg"
          alt=""
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = "/assets/heimdall-logo.svg";
          }}
        />
        <h1>Set your password</h1>
        <p className="muted" style={{ textAlign: "center", fontSize: 13, margin: "0 0 10px" }}>
          {detail
            ? `${detail.email} · joining ${detail.tenant_name}`
            : "Checking your invitation…"}
        </p>

        <label>Password</label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
          autoFocus
        />
        <label>Confirm password</label>
        <input
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          autoComplete="new-password"
        />
        {tooShort ? <div className="err" style={{ marginTop: 8 }}>At least 12 characters.</div> : null}
        {mismatch ? <div className="err" style={{ marginTop: 8 }}>The two passwords do not match.</div> : null}

        <div style={{ marginTop: 16 }}>
          <button
            className="primary"
            type="submit"
            disabled={busy || !detail || password.length < 12 || password !== confirm}
            style={{ width: "100%" }}
          >
            {busy ? "Creating your account…" : "Create account"}
          </button>
        </div>
        {error ? (
          <div className="banner error" style={{ marginTop: 14, marginBottom: 0 }}>
            {error}
          </div>
        ) : null}
      </form>
    </div>
  );
}
